import type { SiteViewer } from './site-viewer'
import { SiteViewerControls, ControlEvent } from '@/lib/site-viewer/siteViewerControls'
import type { Duration } from '@/lib/site-viewer/tween-camera'
import { tweenCamera } from '@/lib/site-viewer/tween-camera'
import type { Vec2Data } from '@/entities/model3d'
import { uvDistance } from '@/lib/site-viewer/utils'

class ParticipantSiteControls extends SiteViewerControls {
  private _killSetInitialTween: () => void = () => {}
  private _killPositionTween: () => void = () => {}
  private _killAllTweens = () => {
    this._killPositionTween()
    this._killSetInitialTween()
  }

  override attach(viewer: SiteViewer) {
    super.attach(viewer)

    if (this.orbitControls) {
      this.orbitControls.enabled = false
    }
  }

  update() {
    this.orbitControls?.update()
  }

  setInitialView({ position, tweenDuration }: { position?: Vec2Data; tweenDuration?: number }): Promise<void> {
    return new Promise(resolve => {
      if (!this.viewer) return resolve()

      this._killAllTweens()
      this._killSetInitialTween = tweenCamera(
        this.viewer.camera,
        { target: position },
        position && this._isEquivalentPosition(position) ? 0 : tweenDuration,
        () => {
          this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
        },
        resolve
      )
    })
  }

  /**
   * Reposition the camera such that we are looking in the direction of the target position by getting the vector
   * between our initial position and the target position and adding it to our target position
   * @param position is the UV coordinate that we would like the camera to be looking in the direction of
   * @param duration is the time in which we spend tweening the initial position to the new position
   */
  lookAt({ position, tweenDuration, onComplete = () => {} }: { position: Vec2Data; tweenDuration?: Duration; onComplete?: () => void }) {
    if (!this.viewer || !position || !this.orbitControls) return

    this._killAllTweens()
    this._killPositionTween = tweenCamera(
      this.viewer.camera,
      { target: position },
      this._isEquivalentPosition(position) ? 0.2 : tweenDuration,
      newPos => {
        this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
        // if we're close, start the next action.
        // the transition can finish while the next action is happening.
        if (uvDistance(newPos, position) < 0.01) {
          onComplete()
        }
      },
      onComplete
    )
  }

  override detach() {
    super.detach()
    this._killAllTweens()
    this.orbitControls?.dispose()
    this.viewer = null
  }

  resetZoom() {}

  startAutoRotate() {}
}

export { ParticipantSiteControls }
